import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import {
  FormControl,
  UntypedFormGroup,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddUnitComponent } from './add-unit/add-unit.component';
import { AdminService } from '../admin.service';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { RolesRightsService } from '../roles-rights/roles-rights.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UnitHistoryDownloadModalComponent } from '../../reporting/reports/unit-profile-history/unit-history-download-modal/unit-history-download-modal.component';
import { SaveUnitProfileDialogComponent } from '../save-unit-profile-dialog/save-unit-profile-dialog.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { GridComponent, GridModule } from '@progress/kendo-angular-grid';
import { UnitGridItem } from '../../models/admin-models';
import { DynamicResponse } from '../../models/general-models';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { NgxLoadingModule } from 'ngx-loading';

@Component({
  selector: 'app-unit-management',
  templateUrl: './unit-management.component.html',
  styleUrls: ['./unit-management.component.scss'],
  imports: [
    NgxLoadingModule,
    UtilityToolbarComponent,
    MatSelectModule,
    MatIconModule,
    GridModule,
    MatChipsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatButtonModule,
    MatInputModule,
  ],
})
export class UnitManagementComponent implements OnInit {
  dialog = inject(MatDialog);
  private adminSrvc = inject(AdminService);
  private rolesService = inject(RolesRightsService);
  private _snackBar = inject(MatSnackBar);
  private ss = inject(SessionStorageService);

  searchForm: FormGroup;
  openIndex;
  searchDisabled = false;
  gridForm: FormGroup;
  gridData: UnitGridItem[];
  noRecordMessage = 'Perform a search to find units';
  filteredRoles: Observable<any[]>;
  roleCtrl = new FormControl();
  ogRoles = [];
  allRoles = ['Administrator', 'AW Rep'];
  roles = [];
  roleData = [];
  roleList;
  deleteRoles = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  unitDetails;
  loading = false;
  rights: Array<string> = [];
  expandedDetailKeys: number[];

  @ViewChild('grid', { static: false }) grid: GridComponent;
  @ViewChild('roleInput', { static: false })
  roleInput: ElementRef<HTMLInputElement>;

  constructor() {
    this.filteredRoles = this.roleCtrl.valueChanges.pipe(
      startWith(null),
      map((role: string | null) =>
        role ? this._filterRoles(role, this.allRoles) : this.allRoles.slice()
      )
    );

    this.rolesService.getRoles().subscribe(roles => {
      this.allRoles = roles['roles'].map(r => r['name']);
      this.roleData = roles['roles'].map(r => {
        return { id: r['id'], name: r['name'] };
      });
    });
  }

  ngOnInit(): void {
    const formGroup = {};
    formGroup['id'] = new FormControl();
    formGroup['name'] = new FormControl();
    formGroup['type'] = new FormControl();
    formGroup['parentUnit'] = new FormControl();
    this.searchForm = new UntypedFormGroup(formGroup);
    this.rights = this.ss.get('rights') || [];
  }

  openCreateUnitDialog(index = null) {
    const dialogRef = this.dialog.open(AddUnitComponent, {});
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveUnit(result['data'], true);
      }
    });
  }

  searchUnits(newUnit = false) {
    this.unitDetails = {};
    this.adminSrvc
      .searchUnits(this.searchForm.value)
      .subscribe((results: DynamicResponse<'units', UnitGridItem[]>) => {
        this.gridData = results.units;
        if (this.gridForm) {
          this.detailExpand({ dataItem: results.units[0] });
        }
        if (newUnit) {
          this.detailExpand({ dataItem: { ...this.gridData[0] }, index: 0 });
        }
      });
  }

  newSearch() {
    this.searchUnits();
    this.resetSearch();
  }

  resetSearch() {
    this.gridData = [];
    this.unitDetails = {};
    this.detailCollapse(null);
  }

  clearSearch() {
    this.gridData = [];
    this.unitDetails = {};
    this.searchForm.reset();
  }

  expandSelected({ dataItem, index }: any): boolean {
    if (dataItem['component']) {
      return index === dataItem['component'].openIndex;
    }
    return false;
  }

  public detailExpand(event: any): void {
    this.unitDetails = null;
    this.gridForm = null;
    this.gridData.forEach((x, i) => {
      this.grid.collapseRow(i);
    });

    this.adminSrvc
      .getUnitDetails(event['dataItem']['UnitId'])
      .subscribe((x: DynamicResponse<'unit', UnitGridItem>) => {
        const data = { ...event['dataItem'], ...x.unit };
        this.expandIndex(data, event['index']);
      });
  }

  expandIndex(data, index) {
    this.openIndex = index;
    data['component'] = this;

    this.roles = data['Roles'] ? data['Roles'].filter(r => r.HasRole > 0) : [];
    this.ogRoles = data['Roles']
      ? data['Roles'].filter(r => r.HasRole > 0)
      : [];
    this.unitDetails = data;
    this.unitDetails.parentList = this.unitDetails.AllowedParents
      ? this.unitDetails.AllowedParents.map(x => x.Name)
      : [];

    this.gridForm = new UntypedFormGroup({
      unitID: new FormControl(data['UnitId']),
      unitName: new FormControl(data['UnitName']),
      unitType: new FormControl(data['UnitType']),
      parentUnit: new FormControl(data['ParentId']),
      roles: new FormControl(this.roles),
    });
  }

  selectedRole(event: MatAutocompleteSelectedEvent): void {
    const addRole = this.roleData.find(x => x.name === event.option.viewValue);

    const formattedRole = {
      ID: addRole.id,
      Name: addRole.name,
      HasRole: 1,
    };

    this.roles.push(formattedRole);

    if (this.deleteRoles || this.deleteRoles.length > 0) {
      this.deleteRoles.forEach(role => {
        if (role.ID === formattedRole.ID) {
          this.deleteRoles = this.deleteRoles.filter(
            roleItem => roleItem.ID !== formattedRole.ID
          );
        }
      });
    }
    this.roleInput.nativeElement.value = '';
    this.roleCtrl.setValue(null);
  }

  addItem(
    event: MatChipInputEvent,
    items: Array<any>,
    control: FormControl
  ): void {
    const input = event.chipInput.inputElement;
    const value = event.value;

    const trimmedValue = (value || '').trim();
    const existingOption = this.roleData.find(
      role => role.name === trimmedValue
    );

    if (existingOption && !items.some(item => item.ID === existingOption.id)) {
      const formattedRole = {
        ID: existingOption.id,
        Name: existingOption.name,
        HasRole: 1,
      };
      items.push(formattedRole);
      this.deleteRoles = this.deleteRoles.filter(
        role => role.ID !== formattedRole.ID
      );
    }

    if (input) {
      input.value = '';
    }

    control.setValue(null);
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: 2000,
    });
  }

  saveUnit(data, createUnit?) {
    const newRoles = this.roles.filter(x => !this.ogRoles.includes(x));
    const deleteRoles = this.deleteRoles;

    if (!createUnit) {
      data.unitID = this.gridForm.controls.unitID.value;
      data.unitName = this.gridForm.controls.unitName.value;
      data.unitType = this.gridForm.controls.unitType.value;

      const dialogRef = this.dialog.open(SaveUnitProfileDialogComponent);
      dialogRef.afterClosed().subscribe({
        next: resp => {
          this.loading = true;
          if (resp?.status === 'continue') {
            this.loading = true;

            if (
              this.unitDetails &&
              this.unitDetails.AllowedParents.find(
                x => x.Name === this.gridForm.controls.parentUnit.value
              )
            ) {
              data.parentUnit = this.unitDetails.AllowedParents.find(
                x => x.Name === this.gridForm.controls.parentUnit.value
              ).ID;
            } else if (
              !data.parentUnit ||
              typeof data.parentUnit === 'undefined'
            ) {
              data.parentUnit = null;
            }
            this.adminSrvc
              .saveUnit(
                data.unitID,
                data.unitName,
                data.unitType,
                data.parentUnit
              )
              .subscribe(() => {
                this.loading = false;
                if (newRoles && newRoles.length > 0) {
                  newRoles.forEach(x =>
                    this.adminSrvc
                      .saveUnitRole(x.ID, data.UnitId, resp.note)
                      .subscribe()
                  );
                } else if (deleteRoles && deleteRoles.length > 0) {
                  deleteRoles.forEach(x =>
                    this.adminSrvc
                      .removeUnitRole(x.ID, data.UnitId, resp.note)
                      .subscribe()
                  );
                }
                this.openSnackBar('Unit Saved');
              });
          } else {
            this.loading = false;
          }
        },
        error: () => {
          this.loading = false;
        },
      });
    } else {
      this.loading = true;
      this.adminSrvc
        .saveUnit(data.unitID, data.unitName, data.unitType, data.parentUnit)
        .subscribe({
          next: resp => {
            this.loading = false;
            if (newRoles && newRoles.length > 0) {
              newRoles.forEach(x =>
                this.adminSrvc.saveUnitRole(x.ID, data.UnitId).subscribe()
              );
            } else if (deleteRoles && deleteRoles.length > 0) {
              deleteRoles.forEach(x =>
                this.adminSrvc
                  .removeUnitRole(x.ID, data.UnitId, resp.note)
                  .subscribe()
              );
            }

            this.resetSearch();
            this.searchForm.controls.id.setValue(data.unitID);
            this.searchUnits(true);
            this.openSnackBar('Unit Created');
          },
          error: () => {
            this.loading = false;
          },
        });
    }
  }

  removeRole(ev, data) {
    const role = data.ID;
    this.roles = this.roles.filter(x => x.ID !== role);
    this.deleteRoles = this.ogRoles.filter(x => !this.roles.includes(x));
  }

  public detailCollapse(event: any): void {
    this.openIndex = -1;
  }

  private _filterRoles(value: string, items: Array<any>): string[] {
    const filterValue = value.toLowerCase();

    return items.filter(unit => unit.toLowerCase().indexOf(filterValue) === 0);
  }

  focusRoles() {
    this.roleInput.nativeElement.blur();
    this.roleInput.nativeElement.focus();
  }

  downloadChangeHistory(unit) {
    let dialogRef = this.dialog.open(UnitHistoryDownloadModalComponent, {
      data: {
        unit: unit.UnitId,
        app: 'rb',
        unitManagement: true,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      dialogRef = null;
    });
  }
}
